import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { authLogin, verifyAuthToken } from "../../services/authService";
import {
  clearInternalRole,
  getInternalRole,
  setInternalRole,
} from "../../helpers/keycloakAuth";
import { setProfile } from "./profileSlice";
import { toast } from "react-toastify";

const access_token = localStorage.getItem("auth_token");

const initialState = {
  session: { user: null, status: "loading", error: null },
  loginPending: false,
  loginError: null,
  loginData: null,
};

// Thunk for login
export const login = createAsyncThunk(
  "auth/login",
  async (
    { redirect = false, type = "user", navigate, location, ...val },
    { dispatch }
  ) => {
    const res = await authLogin({ ...val, type });
    if (res?.status === 200) {
      if (res?.redirectionLink?.accept) {
        if (res.forcePassword && type === "user") {
          navigate("/reset-password", {
            state: { email: val?.email, forcePassword: res?.forcePassword },
          });
        } else {
          navigate("/accept-user");
        }
      } else if (res?.password === 1 || res?.password === 2) {
        navigate("/authentication/donor", {
          state: { email: val?.email, password: res?.password },
        });
        toast.success(res?.message);
      } else {
        const { accessToken, refreshToken, user } = res;
        localStorage.setItem("auth_token", accessToken);
        localStorage.setItem("refreshToken", refreshToken);

        // Mark non-Keycloak auth to prevent KeycloakProvider from clearing tokens
        window.localStorage.setItem("auth_provider", "password");

        if (type === "admin") {
          setInternalRole("admin");
        }

        dispatch(setProfile({ ...user, access_token: accessToken }));
        dispatch(
          setSession({
            user: { ...user, access_token: accessToken },
            status: "authorized",
            error: null,
          })
        );

        const params = new URLSearchParams(location?.search);
        const callback = params.get("callback");

        if (callback) {
          navigate(callback);
        } else {
          navigate(redirect || "/");
        }
      }
    } else if (res?.status === 400) {
      if (res.verified === 2) {
        navigate("/account-verification", { state: { autoCountDown: false } });
      }
    }

    return res;
  }
);

// Thunk for verifying auth token
export const verifyAuth = createAsyncThunk(
  "auth/verifyAuth",
  async (payload = {}, { dispatch }) => {
    const accessToken = localStorage.getItem("auth_token");
    if (!accessToken) {
      dispatch(setProfile(null));
      return {
        user: null,
        status: "unauthorized",
        error: null,
      };
    }
    const requestPayload = { ...(payload || {}) };
    if (!requestPayload.internalRole) {
      const internalRole = getInternalRole();
      if (internalRole) {
        requestPayload.internalRole = internalRole;
      }
    }
    const res = await verifyAuthToken(
      Object.keys(requestPayload).length ? requestPayload : undefined
    );

    if (res?.status === 200) {
      if (requestPayload.internalRole) {
        clearInternalRole();
      }
      dispatch(setProfile({ ...res?.data, access_token: accessToken }));
      localStorage.setItem("userId", res?.data?._id);
      return {
        user: { ...res?.data, access_token: accessToken },
        status: "authorized",
        error: null,
      };
    } else if (res?.data?.invalidToken) {
      localStorage.removeItem("auth_token");
      localStorage.removeItem("refreshToken");
      dispatch(setProfile(null));
      return {
        user: null,
        status: "unauthorized",
        error: "Invalid token",
      };
    } else {
      dispatch(setProfile(null));
      return {
        user: null,
        status: "unauthorized",
        error: null,
      };
    }
  }
);

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    setSession: (state, action) => {
      state.session = { ...state.session, ...action.payload };
    },
    setLoginData: (state, action) => {
      state.loginData = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.loginPending = true;
        state.loginError = null;
        state.loginData = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loginPending = false;
        state.loginData = action.payload;
      })
      .addCase(login.rejected, (state, action) => {
        state.loginPending = false;
        state.loginError = action.error.message;
      })
      .addCase(verifyAuth.fulfilled, (state, action) => {
        state.session = {
          user: action.payload.user,
          status: action.payload.status,
          error: action.payload.error,
        };
      });
  },
});

export const { setSession, setLoginData } = authSlice.actions;
export default authSlice.reducer;
